import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Gauge,
  Bird,
  Plus,
  CurrencyDollar,
  SignOut,
  List,
  Gear,
  CaretLeft,
  CaretRight,
  Users,
  Receipt,
  Building,
  Warehouse,
  UserCircleGear,
} from '@phosphor-icons/react';

const navItems = [
  { href: '/dashboard', label: 'Dashboard', icon: Gauge },
  { href: '/lotes/nuevo', label: 'Nuevo Lote', icon: Plus },
  { href: '/lotes', label: 'Mis Lotes', icon: List },
  { href: '/gastos', label: 'Gastos', icon: CurrencyDollar },
  { href: '/lote-general', label: 'Lote General', icon: Warehouse },
  { href: '/proveedores', label: 'Proveedores', icon: Building },
  { href: '/ventas', label: 'Ventas', icon: Receipt },
  { href: '/nomina', label: 'Nómina', icon: Users },
  { href: '/usuarios', label: 'Gestión Usuarios', icon: UserCircleGear, superuserOnly: true },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen bg-white border-r border-border transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-border">
            {!collapsed ? (
              <Link to="/dashboard" className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_chicken-cost-tracker-1/artifacts/d464t98h_ISOTIPO%20ELANCO%20GROUP%20.png"
                  alt="Elanco Group"
                  className="h-10 w-auto"
                />
                <span className="font-bold text-lg text-[#E73425]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  ELANCO
                </span>
              </Link>
            ) : (
              <Link to="/dashboard">
                <img
                  src="https://customer-assets.emergentagent.com/job_chicken-cost-tracker-1/artifacts/d464t98h_ISOTIPO%20ELANCO%20GROUP%20.png"
                  alt="Elanco Group"
                  className="h-8 w-auto"
                />
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="h-8 w-8"
              data-testid="sidebar-toggle"
            >
              {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {navItems
                .filter(item => !item.superuserOnly || user?.is_superuser)
                .map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`nav-${item.href.replace('/', '')}`}
                    className={cn(
                      "sidebar-link",
                      isActive && "active"
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={20} weight={isActive ? "fill" : "regular"} />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="border-t border-border p-4">
            {!collapsed && (
              <div className="mb-3">
                <p className="text-sm font-medium text-foreground truncate">
                  {user?.nombre}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className={cn(
                "w-full justify-start text-muted-foreground hover:text-destructive",
                collapsed && "justify-center px-2"
              )}
              data-testid="logout-btn"
            >
              <SignOut size={18} />
              {!collapsed && <span className="ml-2">Cerrar Sesión</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={cn(
          "min-h-screen transition-all duration-300",
          collapsed ? "pl-16" : "pl-64"
        )}
      >
        <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

export default Layout;
